import { z } from "zod";

export const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;
export const REFERRAL_CODE_PATTERN_MESSAGE =
  "Use 4–64 letters, numbers, hyphens or underscores";

export const CreateReferralCodeRequestSchema = z.object({
  code: z
    .string()
    .regex(REFERRAL_CODE_PATTERN, REFERRAL_CODE_PATTERN_MESSAGE)
    .optional(),
  max_uses: z
    .number()
    .int("Max uses must be a whole number")
    .min(1, "Max uses must be at least 1")
    .nullable()
    .optional(),
  expires_at: z
    .string()
    .datetime({ offset: true, message: "Expiry must include a timezone (e.g. Z or +HH:MM)" })
    .nullable()
    .optional(),
});

export type CreateReferralCodeRequest = z.infer<typeof CreateReferralCodeRequestSchema>;

export const ReferralCodeSchema = z.object({
  id: z.number(),
  code: z.string(),
  is_active: z.boolean(),
  max_uses: z.number().nullable(),
  used_count: z.number(),
  expires_at: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
});

export type ReferralCode = z.infer<typeof ReferralCodeSchema>;

export const ListedReferralCodeSchema = ReferralCodeSchema.extend({
  remaining_uses: z.number().nullable(),
});

export type ListedReferralCode = z.infer<typeof ListedReferralCodeSchema>;

export type ReferralCodeStatus = "active" | "inactive" | "expired" | "exhausted";

export const deriveReferralCodeStatus = (
  code: Pick<ListedReferralCode, "is_active" | "expires_at" | "remaining_uses">,
  now: Date = new Date()
): ReferralCodeStatus => {
  if (!code.is_active) return "inactive";
  if (code.expires_at !== null && new Date(code.expires_at).getTime() <= now.getTime())
    return "expired";
  if (code.remaining_uses !== null && code.remaining_uses <= 0) return "exhausted";
  return "active";
};
